import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProject } from '../projects/ProjectContext';
import { ProjectInfoCard } from './components/ProjectInfoCard';
import { SuperadminMapScene } from './components/SuperadminMapScene';
import { useSuperadminOverviewStats, type OverviewStats } from './hooks/useSuperadminOverviewStats';
import './superadmin.css';

/* ─── Helpers ────────────────────────────────────────── */

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('es-AR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

/* ─── Sub-components ─────────────────────────────────── */

function StatsGrid({ stats, loading }: { stats: OverviewStats; loading: boolean }): JSX.Element {
  const val = (n: number) => (loading ? '—' : n);
  const cls = `sa-stat-value${loading ? ' sa-stat-value--loading' : ''}`;

  return (
    <section className="sa-stats-grid" aria-label="Estadísticas de plataforma">
      <div className="sa-stat-card">
        <span className={cls}>{val(stats.totalProjects)}</span>
        <span className="sa-stat-label">Proyectos</span>
        <span className="sa-stat-sub">Total en plataforma</span>
      </div>
      <div className="sa-stat-card">
        <span className={cls}>{val(stats.totalClients)}</span>
        <span className="sa-stat-label">Clientes</span>
        <span className="sa-stat-sub">Tenants registrados</span>
      </div>
      <div className="sa-stat-card">
        <span className={cls}>{val(stats.totalUsers)}</span>
        <span className="sa-stat-label">Usuarios</span>
        <span className="sa-stat-sub">En plataforma</span>
      </div>
      <div className="sa-stat-card">
        <span className={cls}>{val(stats.projectsWithNetwork)}</span>
        <span className="sa-stat-label">Con red</span>
        <span className="sa-stat-sub">Proyectos con red territorial</span>
      </div>
    </section>
  );
}

function PlatformStatus({
  stats,
  loading,
}: {
  stats: OverviewStats;
  loading: boolean;
}): JSX.Element {
  const dash = loading ? '—' : null;

  return (
    <section className="sa-platform-status" aria-label="Estado de plataforma">
      <h2 className="sa-platform-status-title">Estado de plataforma</h2>

      <div className="sa-status-list">
        <div className="sa-status-line">
          <span className="sa-status-dot sa-status-dot--active" aria-hidden="true" />
          <span className="sa-status-label">Clientes activos</span>
          <span className="sa-status-value">
            {dash ?? `${stats.activeClients} / ${stats.totalClients}`}
          </span>
        </div>

        <div className="sa-status-line">
          <span className="sa-status-dot sa-status-dot--active" aria-hidden="true" />
          <span className="sa-status-label">Usuarios habilitados</span>
          <span className="sa-status-value">
            {dash ?? `${stats.activeUsers} / ${stats.totalUsers}`}
          </span>
        </div>

        <div className="sa-status-line">
          <span className="sa-status-dot sa-status-dot--active" aria-hidden="true" />
          <span className="sa-status-label">Proyectos en plataforma</span>
          <span className="sa-status-value">{dash ?? stats.totalProjects}</span>
        </div>

        <div className="sa-status-line">
          <span className="sa-status-dot sa-status-dot--active" aria-hidden="true" />
          <span className="sa-status-label">Con red territorial</span>
          <span className="sa-status-value">{dash ?? stats.projectsWithNetwork}</span>
        </div>

        <div className="sa-status-line">
          <span className="sa-status-dot sa-status-dot--muted" aria-hidden="true" />
          <span className="sa-status-label">Última actividad</span>
          <span className="sa-status-value sa-status-value--muted">
            {dash ?? formatDate(stats.lastActivity)}
          </span>
        </div>

        <div className="sa-status-line">
          <span className="sa-status-dot sa-status-dot--pending" aria-hidden="true" />
          <span className="sa-status-label">Cobertura territorial</span>
          <span className="sa-status-value sa-status-value--pending">pendiente</span>
        </div>
      </div>
    </section>
  );
}

/* ─── Main export ────────────────────────────────────── */

/**
 * SuperadminOverview — Pantalla de entrada para rol superadmin.
 *
 * Ruta: /admin/overview
 *
 * @remarks
 * Muestra estadísticas globales, estado de plataforma y mapa territorial
 * interactivo. Al seleccionar un proyecto en el mapa, muestra una ficha
 * flotante con la opción de entrar al proyecto vía ProjectContext.
 * No depende de MapLayout ni ProjectDashboard.
 * Exclusiva para usuarios con rol superadmin.
 */
export function SuperadminOverview(): JSX.Element {
  const { stats, projects, loading, error } = useSuperadminOverviewStats();
  const { setProjectId } = useProject();
  const navigate = useNavigate();
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);

  const selectedProject = projects.find(p => p.id === selectedProjectId) ?? null;

  const handleProjectSelect = useCallback((id: string) => {
    setSelectedProjectId(prev => (prev === id ? null : id));
  }, []);

  const handleEnterProject = useCallback(() => {
    if (!selectedProjectId) return;
    setProjectId(selectedProjectId);
    navigate('/territorio');
  }, [selectedProjectId, setProjectId, navigate]);

  const handleCloseCard = useCallback(() => setSelectedProjectId(null), []);

  return (
    <main className="sa-main">
      <header className="sa-header">
        <span className="sa-header-eyebrow">WESTEER · PLATAFORMA TERRITORIAL</span>
        <h1 className="sa-header-title">Consola Territorial</h1>
        <p className="sa-header-subtitle">
          Vista global de plataforma · Multi-proyecto · Sudamérica
        </p>
      </header>

      {error && (
        <div className="sa-error-banner" role="alert">
          <span className="sa-error-dot" aria-hidden="true" />
          <span>Error al cargar estadísticas: {error}</span>
        </div>
      )}

      <StatsGrid stats={stats} loading={loading} />
      <PlatformStatus stats={stats} loading={loading} />

      <div className="sa-map-wrapper">
        <SuperadminMapScene
          projects={projects}
          loading={loading}
          selectedProjectId={selectedProjectId}
          onProjectSelect={handleProjectSelect}
        />
        {selectedProject && (
          <ProjectInfoCard
            project={selectedProject}
            onEnter={handleEnterProject}
            onClose={handleCloseCard}
          />
        )}
      </div>
    </main>
  );
}
